import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import CartDrawer from './CartDrawer';
import { isAdminRole } from '../utils/authRole';
import { applyImageFallback } from '../utils/imageFallback';

export default function Navbar() {
  const { user, logout, cartCount, cartOpen, setCartOpen } = useApp();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [acctOpen, setAcctOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 900);
  const navigate = useNavigate();
  const loc = useLocation();
  const isAdmin = isAdminRole(user);

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', h);
    return () => window.removeEventListener('scroll', h);
  }, []);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 900);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  useEffect(() => { setMobileOpen(false); setAcctOpen(false); }, [loc]);

  return (
    <>
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 300,
        height: isMobile ? '60px' : 'var(--nav-h)',
        background: scrolled ? 'rgba(250,248,244,0.97)' : 'rgba(250,248,244,0.85)',
        backdropFilter: 'blur(16px)',
        borderBottom: '1px solid rgba(184,146,58,0.12)',
        transition: 'all 0.3s ease',
        boxShadow: scrolled ? '0 2px 24px rgba(26,22,18,0.07)' : 'none',
      }}>
        <div className="container" style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>

          {/* Logo */}
          <Link to="/" style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            <img
              src="/logo.png"
              alt="Glovy Jewellery"
              style={{ height: 'var(--logo-nav-h)', width: 'auto', objectFit: 'contain' }}
              onError={applyImageFallback}
            />
          </Link>

          {/* Centre links */}
          <div className="hide-mob" style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {[['Traditional', '/traditional'], ['Modern', '/modern'], ['Collections', '/collections'], ['About', '/about']].map(([label, path]) => (
              <button key={path} onClick={() => navigate(path)} style={{
                background: 'none', border: 'none', cursor: 'pointer',
                padding: '8px 16px', fontSize: 10.5, letterSpacing: '2px',
                textTransform: 'uppercase', fontFamily: 'var(--font-sans)', fontWeight: 400,
                color: loc.pathname === path ? 'var(--gold)' : 'var(--charcoal)',
                borderBottom: `1.5px solid ${loc.pathname === path ? 'var(--gold)' : 'transparent'}`,
                transition: 'color 0.2s, border-color 0.2s',
              }}
              onMouseEnter={e => e.currentTarget.style.color = 'var(--gold)'}
              onMouseLeave={e => e.currentTarget.style.color = loc.pathname === path ? 'var(--gold)' : 'var(--charcoal)'}
              >{label}</button>
            ))}
          </div>

          {/* Right icons */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
            <NavIcon title="Search" onClick={() => navigate('/traditional')}>
              <svg width="17" height="17" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
            </NavIcon>

            {user ? (
              <>
                <NavIcon title="Wishlist" onClick={() => navigate('/wishlist')}>
                  <svg width="17" height="17" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
                </NavIcon>
                <NavIcon title="Bag" onClick={() => setCartOpen(true)} badge={cartCount}>
                  <svg width="17" height="17" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>
                </NavIcon>
                    {isAdmin && !isMobile && (
                  <button onClick={() => navigate('/admin')}
                    style={{
                      background: 'none', border: '1px solid var(--gold)', cursor: 'pointer',
                      padding: '8px 14px', marginLeft: 6, fontSize: 10,
                      letterSpacing: '2px', textTransform: 'uppercase',
                      color: 'var(--gold)', borderRadius: 999,
                      fontFamily: 'var(--font-sans)', fontWeight: 500
                    }}>
                    Admin Panel
                  </button>
                )}
                <div style={{ position: 'relative' }} className="hide-mob">
                  <NavIcon title="Account" onClick={() => setAcctOpen(o => !o)}>
                    <svg width="17" height="17" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                  </NavIcon>
                  {acctOpen && (
                    <>
                      <div onClick={() => setAcctOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 1 }} />
                      <div style={{ position: 'absolute', top: 'calc(100% + 10px)', right: 0, background: 'white', border: '1px solid var(--border-soft)', borderRadius: 6, minWidth: 190, zIndex: 2, boxShadow: 'var(--shadow-lg)', overflow: 'hidden' }}>
                        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border-soft)', background: 'var(--cream)' }}>
                          <div style={{ fontSize: 14, fontFamily: 'var(--font-serif)' }}>{user.firstName || 'My Account'}</div>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{user.email}</div>
                        </div>
                        {[['My Account', '/account'], ['My Orders', '/orders'], ...(isAdmin ? [['Admin Panel', '/admin']] : [])].map(([l, p]) => (
                          <button key={p} onClick={() => { navigate(p); setAcctOpen(false); }}
                            style={{ display: 'block', width: '100%', textAlign: 'left', padding: '11px 18px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, letterSpacing: '1.5px', textTransform: 'uppercase', color: 'var(--black)', transition: 'background 0.15s' }}
                            onMouseEnter={e => e.target.style.background = 'var(--cream)'}
                            onMouseLeave={e => e.target.style.background = 'none'}
                          >{l}</button>
                        ))}
                        <button onClick={() => { logout(); setAcctOpen(false); navigate('/'); }}
                          style={{ display: 'block', width: '100%', textAlign: 'left', padding: '11px 18px', background: 'none', border: 'none', borderTop: '1px solid var(--border-soft)', cursor: 'pointer', fontSize: 11, letterSpacing: '1.5px', textTransform: 'uppercase', color: '#c0392b' }}>
                          Sign Out
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </>
            ) : (
              <button className="btn btn-primary btn-sm hide-mob" onClick={() => navigate('/login')}>Sign In</button>
            )}

            <button onClick={() => setMobileOpen(o => !o)}
              aria-label="Menu"
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--charcoal)', padding: 6, marginLeft: 4 }}>
              <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                {mobileOpen ? <path d="M18 6 6 18M6 6l12 12"/> : <><line x1="3" y1="7" x2="21" y2="7"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="17" x2="21" y2="17"/></>}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div style={{ background: 'var(--cream)', borderTop: '1px solid var(--border-soft)', padding: '18px 20px', maxHeight: 'calc(100vh - 60px)', overflowY: 'auto' }}>
            {[['Traditional', '/traditional'], ['Modern', '/modern'], ['Collections', '/collections'], ['About', '/about']].map(([l, p]) => (
              <Link key={p} to={p} style={{ display: 'block', padding: '11px 0', fontSize: 12, letterSpacing: '2px', textTransform: 'uppercase', color: 'var(--charcoal)', borderBottom: '1px solid var(--border-soft)' }}>{l}</Link>
            ))}
            {user
              ? <><Link to="/account" style={{ display: 'block', padding: '11px 0', fontSize: 12, letterSpacing: '2px', textTransform: 'uppercase', color: 'var(--charcoal)' }}>My Account</Link>
                  {isAdmin && <Link to="/admin" style={{ display: 'block', padding: '11px 0', fontSize: 12, letterSpacing: '2px', textTransform: 'uppercase', color: 'var(--charcoal)' }}>Admin Panel</Link>}
                  <button onClick={logout} style={{ display: 'block', padding: '11px 0', fontSize: 12, letterSpacing: '2px', textTransform: 'uppercase', color: '#c0392b', background: 'none', border: 'none', cursor: 'pointer' }}>Sign Out</button></>
              : <Link to="/login" style={{ display: 'block', padding: '11px 0', fontSize: 12, letterSpacing: '2px', textTransform: 'uppercase', color: 'var(--gold)' }}>Sign In</Link>
            }
          </div>
        )}
      </nav>
      <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} />
    </>
  );
}

function NavIcon({ children, title, onClick, badge }) {
  return (
    <button onClick={onClick} title={title} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 8, color: 'var(--charcoal)', position: 'relative', transition: 'color 0.2s', borderRadius: '50%' }}
      onMouseEnter={e => e.currentTarget.style.color = 'var(--gold)'}
      onMouseLeave={e => e.currentTarget.style.color = 'var(--charcoal)'}>
      {children}
      {badge > 0 && <span style={{ position: 'absolute', top: 2, right: 2, background: 'var(--gold)', color: 'white', borderRadius: '50%', width: 15, height: 15, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 8, fontWeight: 700 }}>{badge}</span>}
    </button>
  );
}